/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';
import { resourceRoutes } from '@/route-paths';
import type { NavContentSlotResult } from '@/routes/resource.nav-content-slot';
import type { NavContentSlotStore } from '@/components/navigation-menu-mega/nav-content-slot-store';

/**
 * Loads a top-level category's mega-menu content slots on demand (when its dropdown opens) and writes them into
 * the nav content slot store. Each category is fetched at most once per page lifetime; a load interrupted by
 * opening another dropdown is retried the next time that dropdown opens.
 */
export function useNavContentSlotLoader(store: NavContentSlotStore): (categoryId: string) => void {
    const fetcher = useFetcher<NavContentSlotResult>();
    const loadedRef = useRef(new Set<string>());
    const inFlightRef = useRef<string | null>(null);

    useEffect(() => {
        const result = fetcher.data;
        if (fetcher.state !== 'idle' || !result?.categoryId) return;
        loadedRef.current.add(result.categoryId);
        inFlightRef.current = null;
        store.set(result.categoryId, { content: result.content, ribbon: result.ribbon });
    }, [fetcher.data, fetcher.state, store]);

    return useCallback(
        (categoryId: string) => {
            if (!categoryId || loadedRef.current.has(categoryId) || inFlightRef.current === categoryId) return;
            inFlightRef.current = categoryId;
            void fetcher.load(`${resourceRoutes.navContentSlot}?categoryId=${encodeURIComponent(categoryId)}`);
        },
        // fetcher.load is stable across renders; depending on `fetcher` would recreate this on every state change
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
}
